import {useMutation} from '@tanstack/react-query';
import {UseFormReturn} from 'react-hook-form';
import {BackendResponse} from '../../http/backend-response/backend-response';
import {onFormQueryError} from '../../errors/on-form-query-error';
import {useNavigate} from '../../ui/navigation/use-navigate';
import {apiClient} from '../../http/query-client';
import {useAuth} from '../use-auth';
import {setBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {consumeAuthRedirectUri} from '../auth-redirect-uri';

interface Response extends BackendResponse {
  bootstrapData?: string;
  message?: string;
  status: 'success' | 'needs_email_verification';
}

export interface RegisterPayload {
  email: string;
  username: string;
  password: string;
  password_confirmation: string;
  phone: string;
  referrer_code?: string;
}

export function useRegister(
  form: UseFormReturn<RegisterPayload>,
  opts?: {onRegistered?: (response: Response) => Promise<boolean | void> | boolean | void}
) {
  const navigate = useNavigate();
  const {getRedirectUri} = useAuth();

  return useMutation({
    mutationFn: register,
    onSuccess: async response => {
      setBootstrapData(response.bootstrapData!);
      const handled = (await opts?.onRegistered?.(response)) ?? false;
      if (handled === true) {
        return; // custom navigation handled by caller
      }
      if (response.status === 'needs_email_verification') {
        navigate('/');
      } else {
        navigate(consumeAuthRedirectUri() || getRedirectUri(), {replace: true});
      }
    },
    onError: r => onFormQueryError(r, form),
  });
}

function register(payload: RegisterPayload): Promise<Response> {
  return apiClient
    .post('auth/register', payload)
    .then(response => response.data);
}
